import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  IconSearch, IconScan, IconPlus, IconMinus, IconX, IconClock, IconChecks,
  IconArmchair2, IconShoppingBag, IconTruck, IconGlass, IconSquareDot, IconCrown,
  IconFlame, IconChevronLeft, IconChevronRight, IconChevronDown, IconRefresh, IconAdjustmentsHorizontal,
  IconBell, IconMaximize, IconMinimize, IconLogout, IconSun, IconMoon, IconSunHigh, IconUserPlus,
  IconFileInvoice, IconFiles, IconBolt, IconHistory, IconPrinter, IconEyeOff, IconEye,
  IconShoppingCart, IconReceipt2, IconChefHat, IconGridDots, IconChartBar,
  IconSparkles, IconCoffee, IconBeer, IconBowl, IconBread, IconCake, IconCookie, IconLeaf,
  IconStar, IconPencil, IconCash, IconPercentage, IconMail,
} from '@tabler/icons-react';
import { Modal } from '../components/ui';
import { useTheme } from '../hooks/useTheme';
import { useAuth, type UserRole } from '../hooks/useAuth';
import { useRole } from '../hooks/useRole';
import { RequireMinRole } from '../components/RequireRole';
import { vatPortionOfInclusive } from '../lib/vat';
import { BUSINESS_PROFILE } from '../lib/businessProfile';
import Receipt from '../components/Receipt';
import { useDaySession } from '../hooks/useDaySession';
import DayNotOpenOverlay from '../components/DayNotOpenOverlay';
import { OpenDayModal, CloseDayModal } from '../components/DaySessionModals';
import ConnectionStatusBadge from '../components/ConnectionStatusBadge';
import ProductImage from '../components/ProductImage';
import CustomerPopover, { Customer } from '../components/CustomerPopover';
import ProductDetailsModal, { ProductDetails, ProductCustomisation } from '../components/ProductDetailsModal';
import SearchModal, { SearchableItem } from '../components/SearchModal';
import PaymentModal from '../components/PaymentModal';
import PendingRequestsBar, { PendingRequest, PendingCartLine } from '../components/PendingRequestsBar';
import DrawerModal from '../components/DrawerModal';
import OrderActionsModal, { type OrderForActions } from '../components/OrderActionsModal';
import DiscountModal from '../components/DiscountModal';
import PriceOverrideModal from '../components/PriceOverrideModal';
import DayReportModal from '../components/DayReportModal';
import { useMenu, type ApiProduct } from '../hooks/useMenu';
import { useRecentOrders, type ApiRecentOrder } from '../hooks/useRecentOrders';

// =============================================================================
// CATEGORY COLOUR SYSTEM
// One source of truth — each category has a fixed colour applied to:
//   - the category circle in the slider
//   - the product card eyebrow text (bolded, in this colour)
//   - the product card placeholder tile background
// Cohesive visual system across the whole POS.
// =============================================================================

const CATEGORY_COLOURS = {
  hot:      { tone: 'orange',  Icon: IconCoffee,   label: 'Hot Drinks',  count: 18,  iconBg: 'bg-orange-soft text-orange',   eyebrow: 'text-orange',   tile: 'bg-orange'   },
  cold:     { tone: 'info',    Icon: IconBeer,     label: 'Cold Drinks', count: 12,  iconBg: 'bg-info-soft text-info',       eyebrow: 'text-info',     tile: 'bg-info'     },
  food:     { tone: 'success', Icon: IconBowl,     label: 'Food',        count: 24,  iconBg: 'bg-success-soft text-success', eyebrow: 'text-success',  tile: 'bg-success'  },
  toasties: { tone: 'warning', Icon: IconBread,    label: 'Toasties',    count:  8,  iconBg: 'bg-warning-soft text-warning', eyebrow: 'text-warning',  tile: 'bg-warning'  },
  cakes:    { tone: 'pink',    Icon: IconCake,     label: 'Cakes',       count:  9,  iconBg: 'bg-pink-soft text-pink',       eyebrow: 'text-pink',     tile: 'bg-pink'     },
  pastries: { tone: 'purple',  Icon: IconCookie,   label: 'Pastries',    count: 11,  iconBg: 'bg-purple-soft text-purple',   eyebrow: 'text-purple',   tile: 'bg-purple'   },
  sides:    { tone: 'teal',    Icon: IconLeaf,     label: 'Sides',       count:  6,  iconBg: 'bg-teal-soft text-teal',       eyebrow: 'text-teal',     tile: 'bg-teal'     },
} as const;

// CategoryId is now an open string — legacy 7 ids (from CATEGORY_COLOURS
// below) PLUS any cuid returned by /api/categories. The runtime lookup
// table `categoryColours` (computed in the component) merges static
// defaults with live admin-managed ones.
type CategoryId = string;

// Maps a category `tone` value (admin-selectable) to the Tailwind class
// triplet our cards already use. Used when adapting API categories.
const TONE_CLASSES: Record<string, { tile: string; eyebrow: string; iconBg: string }> = {
  orange:  { tile: 'bg-orange',   eyebrow: 'text-orange',  iconBg: 'bg-orange-soft text-orange'   },
  info:    { tile: 'bg-info',     eyebrow: 'text-info',    iconBg: 'bg-info-soft text-info'       },
  success: { tile: 'bg-success',  eyebrow: 'text-success', iconBg: 'bg-success-soft text-success' },
  warning: { tile: 'bg-warning',  eyebrow: 'text-warning', iconBg: 'bg-warning-soft text-warning' },
  pink:    { tile: 'bg-pink',     eyebrow: 'text-pink',    iconBg: 'bg-pink-soft text-pink'       },
  purple:  { tile: 'bg-purple',   eyebrow: 'text-purple',  iconBg: 'bg-purple-soft text-purple'   },
  teal:    { tile: 'bg-teal',     eyebrow: 'text-teal',    iconBg: 'bg-teal-soft text-teal'       },
  danger:  { tile: 'bg-danger',   eyebrow: 'text-danger',  iconBg: 'bg-danger-soft text-danger'   },
  primary: { tile: 'bg-primary',  eyebrow: 'text-primary', iconBg: 'bg-primary-soft text-primary' },
};

// Maps a Tabler icon key (admin-typed string) to a React component.
const ICON_BY_KEY: Record<string, typeof IconSparkles> = {
  coffee: IconCoffee, beer: IconBeer, bowl: IconBowl, bread: IconBread,
  cake: IconCake, cookie: IconCookie, leaf: IconLeaf, sparkles: IconSparkles,
  circle: IconSquareDot,
};

const CATEGORY_LIST: { id: 'all' | string; label: string; count: number; Icon: typeof IconSparkles; iconBg: string }[] = [
  { id: 'all', label: 'All Menus', count: 200, Icon: IconSparkles, iconBg: 'bg-primary text-[#FFFFFF]' },
  ...Object.entries(CATEGORY_COLOURS).map(([id, c]) => ({
    id: id as string,
    label: c.label,
    count: c.count,
    Icon: c.Icon,
    iconBg: c.iconBg,
  })),
];

// =============================================================================
// TYPES
// =============================================================================

interface RecentOrderItem {
  name: string;
  size: string;
  qty: number;
  priceCents: number;
  category: CategoryId;
}
interface RecentOrder {
  id: string;
  apiId?: string;
  customer: string;
  time: string;
  type: 'Dine In' | 'Take Away' | 'Delivery' | 'Table';
  remaining: string;
  delayed: boolean;
  progress: number;
  elapsed: string;
  items: RecentOrderItem[];
  paymentMethod: 'Card' | 'Cash' | 'EFT';
  status: 'Preparing' | 'Ready' | 'Out for delivery' | 'Delayed' | 'Awaiting collection';
}

interface Item {
  id: string;
  name: string;
  description?: string;
  category: CategoryId;
  diet: 'Veg' | 'Non Veg' | 'Egg';
  priceCents: number;
  originalPriceCents?: number;
  imageUrl?: string;
  badge?: 'Trending' | 'Must Try' | 'Hot';
  available: number;
  /**
   * Phase 2.11 — when true, the +/- quick-add is disabled and the cashier
   * is forced into the configurator (modal) before this item lands in the
   * cart. Set on items that genuinely cannot have a sensible default
   * (e.g. "Steak — pick doneness", "Build-your-own").
   */
  requiresConfiguration?: boolean;
  sizes?: { name: string; priceDelta: number }[];
  defaultIngredients?: string[];
  availableAddons?: { name: string; priceCents: number }[];
}

interface CartLine {
  id: string;
  itemId: string;
  name: string;
  category: CategoryId;
  imageUrl?: string;
  size: string;
  qty: number;
  unitPriceCents: number;
  excludedIngredients: string[];
  addedAddons: { name: string; priceCents: number }[];
  note?: string;
  expanded: boolean;
  // Phase 2.6b — supervisor-authorised line price override. When
  // populated, unitPriceCents reflects the override and originalCents
  // preserves the menu price so the chip can show the delta.
  priceOverride?: {
    originalCents: number;
    reason: string;
    approvedBy: { id: string; name: string; role: string };
    token: string;
  };
}

// =============================================================================
// SEED DATA
// =============================================================================

/**
 * Recent orders are now fed by /api/orders/recent (Phase 2.2). The
 * legacy in-memory RECENT array is gone — see useRecentOrders + the
 * adaptApiRecentOrder() mapper inside the component.
 */

/**
 * DEMO_RECENT — shown only when the live API returns zero orders,
 * so the cashier can see what the strip looks like populated. Each
 * entry is rendered with a "DEMO" pill so it can never be mistaken
 * for a real order. Replaced automatically as soon as a real order
 * is placed.
 */
const DEMO_RECENT: RecentOrder[] = [
  {
    id: '#DEMO-1', customer: 'James Smith', time: '11:30 AM', type: 'Delivery',
    remaining: '12 Mins', delayed: false, progress: 10, elapsed: '20:00',
    paymentMethod: 'Card', status: 'Out for delivery',
    items: [
      { name: 'Flat White',    size: 'Regular', qty: 2, priceCents: 3600, category: 'hot' },
      { name: 'Cheese Burger', size: 'Regular', qty: 1, priceCents: 8900, category: 'food' },
    ],
  },
  {
    id: '#DEMO-2', customer: 'Maria Gonzalez', time: '11:45 AM', type: 'Take Away',
    remaining: '-8 Mins', delayed: true, progress: 100, elapsed: '49:33',
    paymentMethod: 'Cash', status: 'Delayed',
    items: [
      { name: 'Avocado Toastie', size: 'Regular', qty: 1, priceCents: 7200, category: 'food' },
      { name: 'Cappuccino',      size: 'Large',   qty: 1, priceCents: 4200, category: 'hot' },
    ],
  },
  {
    id: '#DEMO-3', customer: 'Liam O\'Connor', time: '11:10 AM', type: 'Dine In',
    remaining: '45 Mins', delayed: false, progress: 25, elapsed: '33:00',
    paymentMethod: 'Card', status: 'Preparing',
    items: [
      { name: 'Lemon Mint Juice', size: 'Large', qty: 2, priceCents: 3800, category: 'cold' },
    ],
  },
];

function adaptApiRecentOrder(o: ApiRecentOrder): RecentOrder {
  const placed = new Date(o.placedAt);
  const time = placed.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  const minsAgo = Math.max(0, Math.round((Date.now() - placed.getTime()) / 60000));
  const elapsed = `${String(Math.floor(minsAgo / 60)).padStart(1, '0')}:${String(minsAgo % 60).padStart(2, '0')}`;
  const type: RecentOrder['type'] =
    o.channel === 'TAKEAWAY' ? 'Take Away' :
    o.channel === 'DELIVERY' ? 'Delivery' :
    'Dine In';
  const status: RecentOrder['status'] =
    o.status === 'PREPARING' ? 'Preparing' :
    o.status === 'READY' ? 'Ready' :
    o.status === 'SERVED' ? 'Ready' :
    'Preparing';
  const progress =
    o.status === 'SERVED' ? 100 :
    o.status === 'READY' ? 90 :
    o.status === 'PREPARING' ? 50 :
    20;
  return {
    id: `#${o.orderNumber}`,
    apiId: o.id,
    customer: o.customerName || 'Walk-in',
    time,
    type,
    remaining: '—',
    delayed: false,
    progress,
    elapsed,
    paymentMethod: 'Cash',
    status,
    items: o.lines.map((l) => ({
      name: l.name, size: '—', qty: l.qty, priceCents: 0, category: 'food' as CategoryId,
    })),
  };
}

const CATALOGUE: Item[] = [
  { id: 'A1',  name: 'Flat White',          description: 'Smooth espresso · steamed milk', category: 'hot',     diet: 'Veg',     priceCents: 3200, originalPriceCents: 3800, badge: 'Trending', available: 99,
    sizes: [
      { name: 'Small',   priceDelta: 0 },
      { name: 'Regular', priceDelta: 400 },
      { name: 'Large',   priceDelta: 800 },
    ],
    defaultIngredients: ['Full milk', 'Single shot', 'Sugar'],
    availableAddons: [
      { name: 'Extra shot',         priceCents: 600 },
      { name: 'Decaf',              priceCents: 0 },
      { name: 'Oat milk',           priceCents: 500 },
      { name: 'Vanilla syrup',      priceCents: 700 },
    ],
  },
  { id: 'A2',  name: 'Cheese Burger',       description: 'Beef patty · brioche bun', category: 'food', diet: 'Non Veg', priceCents: 8900, badge: 'Must Try', available: 45,
    sizes: [{ name: 'Regular', priceDelta: 0 }, { name: 'Double', priceDelta: 3000 }],
    defaultIngredients: ['Lettuce', 'Tomato', 'Onion', 'Pickles', 'Cheese', 'Mayo', 'Mustard'],
    availableAddons: [
      { name: 'Extra cheese',  priceCents: 800 },
      { name: 'Bacon',         priceCents: 1500 },
      { name: 'Avocado',       priceCents: 1200 },
      { name: 'Fried egg',     priceCents: 1000 },
    ],
  },
  { id: 'A3',  name: 'Garlic Butter Shrimp', description: 'Pan-seared, herbs', category: 'food', diet: 'Non Veg', priceCents: 9500, available: 30, requiresConfiguration: true,
    sizes: [{ name: 'Half', priceDelta: 0 }, { name: 'Full', priceDelta: 4500 }],
    availableAddons: [{ name: 'Extra garlic butter', priceCents: 1500 }, { name: 'Lemon wedge', priceCents: 0 }],
  },
  { id: 'A4',  name: 'Chicken Wrap',         description: 'Soft tortilla · grilled chicken', category: 'food', diet: 'Non Veg', priceCents: 6500, originalPriceCents: 7800, available: 18,
    defaultIngredients: ['Lettuce', 'Tomato', 'Cheese', 'Mayo', 'Spice rub'],
    availableAddons: [{ name: 'Extra chicken', priceCents: 2000 }, { name: 'Avocado', priceCents: 1200 }, { name: 'Cheese', priceCents: 800 }],
  },
  { id: 'A5',  name: 'Tomato Basil Soup',    description: 'Slow-roasted tomato · cream', category: 'food', diet: 'Veg', priceCents: 4200, available: 22 },
  { id: 'A6',  name: 'Vegetable Roll',       description: 'Sesame · sweet chilli', category: 'food', diet: 'Veg', priceCents: 2800, badge: 'Trending', available: 14 },
  { id: 'A7',  name: 'Lemon Mint Juice',     description: 'Fresh, ice-cold', category: 'cold', diet: 'Veg', priceCents: 1800, available: 50,
    sizes: [{ name: 'Regular', priceDelta: 0 }, { name: 'Large', priceDelta: 800 }],
  },
  { id: 'A8',  name: 'Grilled Chicken',      description: 'Lemon · herbs · sides', category: 'food', diet: 'Non Veg', priceCents: 8200, badge: 'Must Try', available: 12 },
  { id: 'A9',  name: 'Caesar Salad',         description: 'Crisp lettuce · parmesan · croutons', category: 'food', diet: 'Veg', priceCents: 5600, available: 25,
    defaultIngredients: ['Romaine lettuce', 'Parmesan', 'Croutons', 'Caesar dressing', 'Anchovies'],
    availableAddons: [{ name: 'Grilled chicken', priceCents: 2500 }, { name: 'Halloumi', priceCents: 2000 }],
  },
  { id: 'A10', name: 'Cappuccino',           description: 'Espresso · foamed milk', category: 'hot', diet: 'Veg', priceCents: 3200, available: 99,
    sizes: [{ name: 'Small', priceDelta: 0 }, { name: 'Regular', priceDelta: 400 }, { name: 'Large', priceDelta: 800 }],
    defaultIngredients: ['Full milk', 'Single shot', 'Cocoa dust'],
    availableAddons: [{ name: 'Extra shot', priceCents: 600 }, { name: 'Decaf', priceCents: 0 }, { name: 'Oat milk', priceCents: 500 }],
  },
  { id: 'A11', name: 'Iced Latte',           description: 'Cold milk · ice · double shot', category: 'cold', diet: 'Veg', priceCents: 3800, available: 50,
    sizes: [{ name: 'Regular', priceDelta: 0 }, { name: 'Large', priceDelta: 600 }],
    availableAddons: [{ name: 'Extra shot', priceCents: 600 }, { name: 'Vanilla syrup', priceCents: 700 }, { name: 'Caramel syrup', priceCents: 700 }],
  },
  { id: 'A12', name: 'Avocado Toastie',      description: 'Sourdough · feta · seeds', category: 'toasties', diet: 'Veg', priceCents: 6500, badge: 'Hot', available: 20,
    defaultIngredients: ['Sourdough', 'Avocado', 'Feta', 'Mixed seeds', 'Lemon zest', 'Chilli flakes'],
    availableAddons: [{ name: 'Poached egg', priceCents: 1200 }, { name: 'Bacon', priceCents: 1500 }, { name: 'Halloumi', priceCents: 2000 }],
  },
];

function fmtR(cents: number) {
  return `R ${(cents / 100).toFixed(2)}`;
}

// =============================================================================
// SMALL PRIMITIVES
// =============================================================================

function Stepper({ qty, onInc, onDec, size = 'md' }: { qty: number; onInc: () => void; onDec: () => void; size?: 'sm' | 'md' }) {
  // Touch-friendly on tablets: bump to 40px at lg+ so a finger lands cleanly
  // (44px is iOS HIG guidance; 40 plus border = ~42 effective, fine for cafe use).
  const dim = size === 'sm' ? 'w-8 h-8 lg:w-10 lg:h-10' : 'w-9 h-9 lg:w-11 lg:h-11';
  return (
    <div className="inline-flex items-center bg-light rounded-md border border-border overflow-hidden">
      <button onClick={onDec} disabled={qty <= 0} className={`${dim} inline-flex items-center justify-center text-gray-700 hover:bg-gray-200 disabled:opacity-30 transition-colors`}>
        <IconMinus size={14} stroke={2.5} />
      </button>
      <span className="px-2.5 lg:px-3 text-14 lg:text-15 font-bold tabular-nums text-heading min-w-[24px] text-center">{qty}</span>
      <button onClick={onInc} className={`${dim} inline-flex items-center justify-center text-primary hover:bg-primary-soft transition-colors`}>
        <IconPlus size={14} stroke={2.5} />
      </button>
    </div>
  );
}

interface CategoryColour { tile: string; eyebrow: string; iconBg: string; label: string; Icon: typeof IconSparkles }

function ProductCard({ item, qty, onInc, onDec, onOpen, categoryColours }: { item: Item; qty: number; onInc: () => void; onDec: () => void; onOpen: () => void; categoryColours: Record<string, CategoryColour> }) {
  const cat = categoryColours[item.category] ?? { tile: 'bg-light', eyebrow: 'text-gray-700', iconBg: 'bg-light text-gray-700', label: '', Icon: IconSparkles };
  const dietColour = item.diet === 'Veg' ? 'text-success' : item.diet === 'Non Veg' ? 'text-danger' : 'text-warning';
  const badgeTone = item.badge === 'Trending' ? 'bg-danger' : item.badge === 'Must Try' ? 'bg-indigo' : 'bg-orange';
  const badgeIcon = item.badge === 'Trending' ? <IconCrown size={11} /> : <IconFlame size={11} />;

  // Phase 2.11 — customisation signalling.
  const hasOptions =
    (item.sizes?.length ?? 0) > 1
    || (item.defaultIngredients?.length ?? 0) > 0
    || (item.availableAddons?.length ?? 0) > 0;
  const mustConfigure = !!item.requiresConfiguration;
  const soldOut = (item.available ?? 99) <= 0;

  return (
    <article className={`bg-surface rounded-lg border border-border p-2 transition-shadow hover:shadow-sm ${soldOut ? 'opacity-60' : ''}`}>
      <div className="relative mb-2">
        <button onClick={soldOut ? undefined : onOpen} disabled={soldOut} className="block w-full h-[112px] rounded-md overflow-hidden disabled:cursor-not-allowed">
          <ProductImage src={item.imageUrl} name={item.name} seed={item.id} bgClassName={cat.tile} />
        </button>
        {soldOut && (
          <span className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <span className="inline-flex items-center px-3 h-8 rounded-md bg-danger text-[#FFFFFF] text-13 font-bold uppercase tracking-widest shadow-md">
              Sold out
            </span>
          </span>
        )}
        {item.badge && !soldOut && (
          <span className={`absolute top-2 left-2 inline-flex items-center gap-1 px-2 h-6 rounded-md text-11 font-bold uppercase tracking-wider text-[#FFFFFF] shadow-sm ${badgeTone}`}>
            {badgeIcon}
            {item.badge}
          </span>
        )}
        {/* Customisation hint chip — bottom-right corner of the image.
            "Pick options" when configuration is required; "Customisable" when optional. */}
        {(mustConfigure || hasOptions) && (
          <span
            className={[
              'absolute bottom-2 right-2 inline-flex items-center gap-1 px-2 h-6 rounded-md text-11 font-bold uppercase tracking-wider shadow-sm',
              mustConfigure
                ? 'bg-danger text-[#FFFFFF]'
                : 'bg-surface text-heading border border-border',
            ].join(' ')}
          >
            <IconAdjustmentsHorizontal size={11} stroke={2.5} />
            {mustConfigure ? 'Pick options' : 'Customisable'}
          </span>
        )}
      </div>
      <div>
        <div className="flex items-center justify-between mb-1">
          <p className={`text-12 font-bold uppercase tracking-wide ${cat.eyebrow}`}>{cat.label}</p>
          <span className={`inline-flex items-center gap-0.5 text-12 font-medium ${dietColour}`}>
            <IconSquareDot size={11} stroke={2.5} fill="currentColor" />
            {item.diet}
          </span>
        </div>
        <h6 className="text-14 font-semibold text-heading truncate mb-2">
          <button onClick={onOpen} className="hover:text-primary transition-colors">{item.name}</button>
        </h6>
        <div className="flex items-center justify-between gap-2">
          <p className="flex items-baseline gap-1.5">
            {item.originalPriceCents && (
              <span className="text-12 text-gray-500 line-through tabular-nums">{fmtR(item.originalPriceCents)}</span>
            )}
            <span className="text-15 font-bold text-heading tabular-nums">{fmtR(item.priceCents)}</span>
          </p>
          {soldOut ? (
            <span className="text-11 font-bold uppercase tracking-wider text-danger">Sold out</span>
          ) : mustConfigure ? (
            <button
              type="button"
              onClick={onOpen}
              className="h-9 lg:h-11 px-3 lg:px-4 inline-flex items-center gap-1.5 rounded-md bg-primary text-[#FFFFFF] text-12 lg:text-13 font-bold uppercase tracking-wider hover:bg-primary-hover transition-colors"
            >
              <IconAdjustmentsHorizontal size={12} stroke={2.5} />
              Configure
            </button>
          ) : (
            <Stepper qty={qty} onInc={onInc} onDec={onDec} size="sm" />
          )}
        </div>
      </div>
    </article>
  );
}

function RecentOrderCard({ o, onOpen, isDemo }: { o: RecentOrder; onOpen: () => void; isDemo?: boolean }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className={[
        'rounded-lg border p-3 min-w-[290px] snap-start text-left bg-surface hover:shadow-md transition-all active:scale-[0.99]',
        isDemo
          ? 'border-dashed border-warning/40 hover:border-warning'
          : 'border-border hover:border-primary',
      ].join(' ')}
    >
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="leading-tight">
          <div className="flex items-center gap-1.5 mb-0.5">
            <p className="text-14 font-bold text-heading tabular-nums">{o.id}</p>
            {isDemo && (
              <span className="px-1.5 py-0.5 rounded text-9 font-bold uppercase tracking-widest bg-warning text-[#FFFFFF]">Demo</span>
            )}
          </div>
          <h6 className="text-15 font-bold text-heading">{o.customer}</h6>
          <p className="text-13 font-semibold text-heading mt-0.5">{o.time}</p>
        </div>
        <div className="flex flex-col items-end gap-1.5">
          <span className="inline-flex items-center gap-1 px-2.5 h-7 rounded-md bg-light text-12 font-bold text-heading border border-border">
            <IconChecks size={12} stroke={2.5} />
            {o.type}
          </span>
          <span className={`inline-flex items-center gap-1 px-2.5 h-7 rounded-pill text-13 font-bold text-[#FFFFFF] shadow-sm ${o.delayed ? 'bg-danger' : 'bg-success'}`}>
            <IconClock size={12} stroke={2.5} />
            {o.remaining}
          </span>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <div className="flex-1 h-1.5 rounded-full bg-light overflow-hidden">
          <div className={`h-full rounded-full ${o.delayed ? 'bg-danger' : 'bg-success'}`} style={{ width: `${o.progress}%` }} />
        </div>
        <p className="inline-flex items-center gap-1 text-12 tabular-nums text-heading font-bold">
          <IconClock size={12} stroke={2.5} /> {o.elapsed}
        </p>
      </div>
    </button>
  );
}

function DietToggle({ label, dotColor, active, onClick }: { label: string; dotColor: string; active: boolean; onClick: () => void }) {
  return (
    <label className="inline-flex items-center gap-2 cursor-pointer select-none px-1">
      <input type="checkbox" checked={active} onChange={onClick} className="w-4 h-4 rounded accent-primary cursor-pointer" />
      <span className={`w-3 h-3 rounded-sm border-2 ${dotColor}`} />
      <span className="text-14 font-medium text-heading">{label}</span>
    </label>
  );
}

// =============================================================================
// TOP-BAR UTILITY BUTTONS
// =============================================================================

/**
 * Fullscreen toggle. Uses the Fullscreen API; falls back gracefully if
 * unsupported (Safari iOS PWA). The icon flips to "minimize" when active.
 */
function FullscreenButton() {
  const [isFull, setIsFull] = useState(false);
  useEffect(() => {
    const onChange = () => setIsFull(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);
  async function toggle() {
    try {
      if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen?.();
      } else {
        await document.exitFullscreen?.();
      }
    } catch { /* swallow — Safari iOS often refuses */ }
  }
  return (
    <button
      onClick={toggle}
      className="w-9 h-9 inline-flex items-center justify-center rounded-md text-gray-700 hover:bg-surface hover:text-primary transition-colors"
      title={isFull ? 'Exit fullscreen' : 'Enter fullscreen'}
      aria-label="Toggle fullscreen"
    >
      {isFull ? <IconMinimize size={18} stroke={1.75} /> : <IconMaximize size={18} stroke={1.75} />}
    </button>
  );
}

/**
 * Notifications. Until Phase 8 ships real events, the count is
 * computed from things-needing-attention surfaced by existing
 * endpoints:
 *   - low-stock products (/api/stock with flagged)
 *   - voided / refunded orders today (informational, not actionable)
 * Click opens a popover listing what's pending.
 */
function NotificationsButton() {
  const [open, setOpen] = useState(false);
  // Stock notifications disabled until the ingredient-level inventory ships
  // (Phase 5). The per-product `available` count is a placeholder and was
  // surfacing hundreds of false-positive "low stock" entries.
  const lowStock: { productId: string; name: string; onHand: number }[] = [];
  const count = lowStock.length;

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="relative w-9 h-9 inline-flex items-center justify-center rounded-md text-gray-700 hover:bg-surface hover:text-primary transition-colors"
        aria-label="Notifications"
        title={count > 0 ? `${count} item${count === 1 ? '' : 's'} need attention` : 'No notifications'}
      >
        <IconBell size={18} stroke={1.75} />
        {count > 0 && (
          <span className="absolute top-1 right-1 min-w-[14px] h-[14px] px-1 inline-flex items-center justify-center rounded-full bg-danger text-[#FFFFFF] text-9 font-bold tabular-nums ring-2 ring-light">
            {count > 9 ? '9+' : count}
          </span>
        )}
      </button>
      {open && (
        <>
          <button
            type="button"
            aria-label="Close"
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-[60] cursor-default"
          />
          {/* Fixed positioning so the popover escapes the header's stacking
              context — otherwise the cart aside's transform creates a sibling
              stacking context that visually obscures the popover. */}
          <div className="fixed top-[64px] right-3 w-[min(420px,calc(100vw-1.5rem))] max-h-[60vh] overflow-y-auto z-[70] rounded-lg bg-surface border border-border shadow-xl">
            <div className="p-3 border-b border-border flex items-center justify-between">
              <p className="text-13 font-bold uppercase tracking-wider text-gray-700">Notifications</p>
              <span className="text-12 font-bold text-heading tabular-nums">{count}</span>
            </div>
            {count === 0 ? (
              <div className="p-5 text-center">
                <p className="text-14 font-bold text-heading">You're all caught up</p>
                <p className="text-12 text-gray-700 mt-1">Stock notifications will return when ingredient-level inventory is in place.</p>
              </div>
            ) : (
              <ul>
                {lowStock.map((p) => (
                  <li key={p.productId} className="px-3 py-2.5 border-b border-border-soft text-13">
                    <p className="font-bold text-heading">Low stock: {p.name}</p>
                    <p className="text-12 font-medium text-gray-700">On hand: <span className="text-danger font-bold tabular-nums">{p.onHand}</span></p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// =============================================================================
// MAIN PAGE
// =============================================================================

export default function PosPage() {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const { auth, signOut } = useAuth();
  const { hasMinRole } = useRole();
  const cashierInitials = (auth?.cashierName ?? 'D').split(' ').map((p) => p[0]).join('').slice(0, 2).toUpperCase();
  const lockOut = () => { signOut(); navigate('/login', { replace: true }); };

  const [activeCategory, setActiveCategory] = useState<'all' | CategoryId>('all');
  const [search] = useState('');
  const [orderType, setOrderType] = useState<'Dine In' | 'Take Away' | 'Delivery' | 'Table'>('Dine In');
  const [recentOpen, setRecentOpen] = useState(true);
  const [cartOpen, setCartOpen] = useState(false);  // mobile cart drawer

  // Live clock — refreshed every 30s. Renders the order panel header
  // ("Order · 13 May, 2026 · 12:44 PM") so it stays accurate while the
  // till is open without re-rendering every second.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 30_000);
    return () => window.clearInterval(id);
  }, []);
  const nowLabel = `${now.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' })} · ${now.toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit', timeZone: 'Africa/Johannesburg' })}`;
  const [tableId, setTableId] = useState<string | null>(null);
  const [tables, setTables] = useState<{ id: string; name: string; code: string; area: string | null }[]>([]);
  const [recentFilter, setRecentFilter] = useState<'All Orders' | 'Dine In' | 'Take Away' | 'Delivery' | 'Table'>('All Orders');
  const [diet, setDiet] = useState({ veg: true, nonVeg: false, egg: false });
  const [moreActionsOpen, setMoreActionsOpen] = useState(false);

  // Customer attached to this order — null = walk-in
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [customerOpen, setCustomerOpen] = useState(false);

  // Auto-attach customer returning from /onboard-customer (the
  // "flip the iPad" loyalty signup). The onboarding page drops the
  // new customer object into sessionStorage; we pick it up on mount,
  // attach to the open order, and clear it.
  useEffect(() => {
    const raw = sessionStorage.getItem('pos.pending-customer-attach');
    if (!raw) return;
    try {
      const c: Customer = JSON.parse(raw);
      setCustomer(c);
    } catch { /* ignore */ }
    sessionStorage.removeItem('pos.pending-customer-attach');
  }, []);

  // Pull the table list once for the order-type picker (Phase 6.1).
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch('/api/tables', { credentials: 'include' });
        if (!r.ok) return;
        const data = await r.json();
        setTables(data.tables ?? []);
      } catch { /* ignore */ }
    })();
  }, []);

  // Product details modal state. When `editingLineId` is set, the
  // modal opens pre-populated with that line's customisation and the
  // CTA reads "Update" instead of "Add" — submit replaces the line.
  const [detailsItem, setDetailsItem] = useState<Item | null>(null);
  const [editingLineId, setEditingLineId] = useState<string | null>(null);

  // Recent-order preview modal — read-only summary of an order.
  const [previewOrder, setPreviewOrder] = useState<RecentOrder | null>(null);
  // Receipt-preview modal — opened from the recent-order modal's
  // "Print receipt" button. Renders the SARS-compliant receipt and
  // can trigger window.print().
  const [receiptOrder, setReceiptOrder] = useState<RecentOrder | null>(null);

  // Day-session modals — supervisor+ flow
  const { current: daySession } = useDaySession();
  const [openDayModalOpen, setOpenDayModalOpen] = useState(false);
  const [closeDayModalOpen, setCloseDayModalOpen] = useState(false);
  const [drawerModalOpen, setDrawerModalOpen] = useState(false);
  const [discountModalOpen, setDiscountModalOpen] = useState(false);
  const [orderDiscountCents, setOrderDiscountCents] = useState(0);
  const [orderDiscountReason, setOrderDiscountReason] = useState('');
  // Phase 2.6b — which cart line (if any) is being price-overridden.
  const [overrideLineId, setOverrideLineId] = useState<string | null>(null);
  const [xReportOpen, setXReportOpen] = useState(false);
  const [actionsOrder, setActionsOrder] = useState<OrderForActions | null>(null);
  const [openingActionsFor, setOpeningActionsFor] = useState<string | null>(null);

  async function openActionsForApiOrderId(apiOrderId: string) {
    setOpeningActionsFor(apiOrderId);
    try {
      const r = await fetch(`/api/orders/${apiOrderId}`, { credentials: 'include' });
      if (!r.ok) return;
      const data = await r.json();
      setActionsOrder({
        id: data.order.id,
        orderNumber: data.order.orderNumber,
        status: data.order.status,
        totalCents: data.order.totalCents,
        payments: data.order.payments.map((p: { id: string; method: string; amountCents: number; refundedAmountCents: number }) => ({
          id: p.id,
          method: p.method as OrderForActions['payments'][number]['method'],
          amountCents: p.amountCents,
          refundedAmountCents: p.refundedAmountCents ?? 0,
        })),
      });
    } finally {
      setOpeningActionsFor(null);
    }
  }

  // Command-palette product search.
  const [searchOpen, setSearchOpen] = useState(false);

  // searchableItems is computed later, after liveCatalogue is built.

  // Global hotkeys: `/` or ⌘K opens search.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const inField = target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable);
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      } else if (e.key === '/' && !inField) {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // Refs for scroll buttons
  const recentScrollRef = useRef<HTMLDivElement>(null);
  const categoryScrollRef = useRef<HTMLDivElement>(null);
  const scrollByX = (ref: React.RefObject<HTMLDivElement>, dx: number) => {
    ref.current?.scrollBy({ left: dx, behavior: 'smooth' });
  };

  const [cart, setCart] = useState<CartLine[]>([]);

  // ---------------------------------------------------------------------------
  // Live menu (Phase 4.2) — merges admin-managed categories + products with
  // the legacy CATALOGUE seed so an empty back-office doesn't leave the till
  // blank during the transition. Once admin adds products + categories, the
  // API data wins.
  // ---------------------------------------------------------------------------
  const { categories: apiCategories, products: apiProducts, loading: menuLoading } = useMenu();

  function adaptApiProduct(p: ApiProduct): Item {
    return {
      id: p.id,
      name: p.name,
      description: p.description ?? undefined,
      category: p.categoryId,
      diet: p.diet === 'NON_VEG' ? 'Non Veg' : p.diet === 'EGG' ? 'Egg' : 'Veg',
      priceCents: p.priceCents,
      originalPriceCents: p.originalPriceCents ?? undefined,
      imageUrl: p.imageUrl ?? undefined,
      badge: p.badge === 'TRENDING' ? 'Trending' : p.badge === 'MUST_TRY' ? 'Must Try' : p.badge === 'HOT' ? 'Hot' : undefined,
      available: p.available,
      requiresConfiguration: p.requiresConfiguration,
      sizes: p.sizes,
      defaultIngredients: p.defaultIngredients,
      availableAddons: p.availableAddons,
    };
  }

  // Live catalogue + category colour table. When API has nothing yet,
  // fall back to the static seed so the till is never empty.
  // Never show the seed CATALOGUE if the menu is still loading OR if the
  // API has returned at all (even empty). Seed data was for the pre-API
  // era and is the source of the "flash of demo products" on refresh.
  const liveCatalogue: Item[] = apiProducts.length > 0
    ? apiProducts.map(adaptApiProduct)
    : (menuLoading ? CATALOGUE.slice(0, 0) : CATALOGUE.slice(0, 0));

  const liveCategoryColours: Record<string, { tile: string; eyebrow: string; iconBg: string; label: string; Icon: typeof IconSparkles }> = (() => {
    if (apiCategories.length > 0) {
      const out: typeof liveCategoryColours = {};
      for (const c of apiCategories) {
        const tone = TONE_CLASSES[c.tone] ?? TONE_CLASSES.primary;
        out[c.id] = {
          tile: tone.tile,
          eyebrow: tone.eyebrow,
          iconBg: tone.iconBg,
          label: c.name,
          Icon: ICON_BY_KEY[c.iconKey] ?? IconSparkles,
        };
      }
      return out;
    }
    // Legacy fallback — wrap the static CATEGORY_COLOURS keys
    const out: typeof liveCategoryColours = {};
    for (const [k, v] of Object.entries(CATEGORY_COLOURS)) {
      out[k] = { tile: v.tile, eyebrow: v.eyebrow, iconBg: v.iconBg, label: v.label, Icon: v.Icon };
    }
    return out;
  })();

  const liveCategoryList: { id: 'all' | string; label: string; count: number; Icon: typeof IconSparkles; iconBg: string }[] = (() => {
    if (apiCategories.length > 0) {
      const sorted = [...apiCategories].sort((a, b) => a.sortOrder - b.sortOrder);
      const counts: Record<string, number> = {};
      for (const p of apiProducts) counts[p.categoryId] = (counts[p.categoryId] ?? 0) + 1;
      const total = apiProducts.length;
      return [
        { id: 'all', label: 'All Menus', count: total, Icon: IconSparkles, iconBg: 'bg-primary text-[#FFFFFF]' },
        ...sorted.map((c) => ({
          id: c.id,
          label: c.name,
          count: counts[c.id] ?? 0,
          Icon: ICON_BY_KEY[c.iconKey] ?? IconSparkles,
          iconBg: TONE_CLASSES[c.tone]?.iconBg ?? TONE_CLASSES.primary.iconBg,
        })),
      ];
    }
    return CATEGORY_LIST;
  })();

  // Build the SearchModal payload from the live catalogue + colours.
  const searchableItems: SearchableItem[] = liveCatalogue.map((it) => {
    const c = liveCategoryColours[it.category] ?? { label: '', eyebrow: 'text-gray-700', tile: 'bg-light' };
    return {
      id: it.id,
      name: it.name,
      description: it.description,
      category: it.category,
      categoryLabel: c.label,
      categoryEyebrow: c.eyebrow,
      categoryTile: c.tile,
      priceCents: it.priceCents,
      originalPriceCents: it.originalPriceCents,
      imageUrl: it.imageUrl,
      badge: it.badge,
    };
  });

  const [orderModal, setOrderModal] = useState(false);
  const [receiptStep, setReceiptStep] = useState<'choose' | 'email-input' | 'sent' | 'printed'>('choose');
  const [receiptEmail, setReceiptEmail] = useState('');
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [lastPlacedOrder, setLastPlacedOrder] = useState<{ orderNumber: number; totalCents: number } | null>(null);
  const { orders: apiRecent, refresh: refreshRecent, loading: recentLoading } = useRecentOrders();

  // Filter products
  const visible = liveCatalogue.filter((it) => {
    const catOk = activeCategory === 'all' || it.category === activeCategory;
    const searchOk = search === '' || it.name.toLowerCase().includes(search.toLowerCase());
    return catOk && searchOk;
  });

  const recentAdapted = apiRecent.map(adaptApiRecentOrder);
  // Demo recents were the source of the "flash of fake orders" on refresh.
  // We only fall back to DEMO_RECENT when the API has confirmed zero
  // orders (recentLoading=false AND empty). While loading we render
  // nothing — better empty than fake.
  const usingDemo = !recentLoading && recentAdapted.length === 0;
  const recentForDisplay = usingDemo ? DEMO_RECENT : recentAdapted;
  const visibleRecent = recentForDisplay.filter((o) => recentFilter === 'All Orders' || o.type === recentFilter);

  // Cart math
  const qtyOf = (id: string) => cart.find((l) => l.itemId === id)?.qty ?? 0;
  const incLine = (id: string) => setCart((c) => c.map((l) => l.id === id ? { ...l, qty: l.qty + 1 } : l));
  const decLine = (id: string) => setCart((c) => {
    const line = c.find((l) => l.id === id);
    if (!line) return c;
    if (line.qty <= 1) return c.filter((l) => l.id !== id);
    return c.map((l) => l.id === id ? { ...l, qty: l.qty - 1 } : l);
  });
  const removeLine = (id: string) => setCart((c) => c.filter((l) => l.id !== id));
  const toggleExpand = (id: string) => setCart((c) => c.map((l) => l.id === id ? { ...l, expanded: !l.expanded } : l));

  // Phase 2.6b — supervisor-authorised price override. The cart line's
  // unitPriceCents becomes the override price; originalCents is
  // captured for the chip + audit. The token is opaque to the client
  // and is passed straight back to the server on order POST.
  const applyPriceOverride = (result: {
    lineId: string;
    newUnitPriceCents: number;
    originalUnitPriceCents: number;
    reason: string;
    approvedBy: { id: string; name: string; role: string };
    token: string;
  }) => {
    setCart((c) => c.map((l) => l.id === result.lineId
      ? {
          ...l,
          unitPriceCents: result.newUnitPriceCents,
          priceOverride: {
            originalCents: result.originalUnitPriceCents,
            reason: result.reason,
            approvedBy: result.approvedBy,
            token: result.token,
          },
        }
      : l,
    ));
  };
  const removePriceOverride = (lineId: string) => {
    setCart((c) => c.map((l) => l.id === lineId && l.priceOverride
      ? { ...l, unitPriceCents: l.priceOverride.originalCents, priceOverride: undefined }
      : l,
    ));
  };
  const overrideLine = overrideLineId ? cart.find((l) => l.id === overrideLineId) ?? null : null;

  // Inline qty stepper on product card — quick add with no customisations.
  // Items marked requiresConfiguration MUST go through the modal — the
  // ProductCard already hides the stepper for those, but this is the
  // defensive belt-and-braces check.
  const quickInc = (it: Item) => {
    if (it.requiresConfiguration) {
      setDetailsItem(it);
      return;
    }
    setCart((c) => {
      const ex = c.find((l) => l.itemId === it.id && l.excludedIngredients.length === 0 && l.addedAddons.length === 0);
      if (ex) return c.map((l) => l.id === ex.id ? { ...l, qty: l.qty + 1 } : l);
      const defaultSize = it.sizes?.[0]?.name ?? 'Regular';
      const defaultPrice = it.priceCents + (it.sizes?.[0]?.priceDelta ?? 0);
      return [...c, {
        id: crypto.randomUUID(), itemId: it.id, name: it.name, category: it.category,
        size: defaultSize, qty: 1, unitPriceCents: defaultPrice,
        excludedIngredients: [], addedAddons: [], expanded: false,
      }];
    });
  };
  const quickDec = (it: Item) => {
    setCart((c) => {
      const ex = c.find((l) => l.itemId === it.id && l.excludedIngredients.length === 0 && l.addedAddons.length === 0);
      if (!ex) return c;
      if (ex.qty <= 1) return c.filter((l) => l.id !== ex.id);
      return c.map((l) => l.id === ex.id ? { ...l, qty: l.qty - 1 } : l);
    });
  };

  // Add a customised line from the ProductDetailsModal
  const addCustomised = (item: Item, cust: ProductCustomisation) => {
    setCart((c) => [...c, {
      id: crypto.randomUUID(),
      itemId: item.id,
      name: item.name,
      category: item.category,
      size: cust.size,
      qty: cust.qty,
      unitPriceCents: cust.unitPriceCents,
      excludedIngredients: cust.excludedIngredients,
      addedAddons: cust.addedAddons,
      note: cust.note || undefined,
      expanded: true,
    }]);
  };

  // Re-open the modifier modal for an existing line. Looks up the
  // original Item from the catalogue (cart line stores itemId) and
  // sets editingLineId so submit replaces instead of appending.
  // Load an accepted TableRequest's cart snapshot into the till's cart.
  // Each snapshot line becomes a real CartLine that the cashier can edit
  // exactly like any line they added themselves.
  const acceptedRequestRef = useRef<{ id: string; orderCode: string; table: { name: string } } | null>(null);
  const handleAcceptRequest = (request: PendingRequest) => {
    if (request.kind === 'ASSISTANCE' || !request.cartSnapshot) {
      // Assistance request: just acknowledge — no cart import.
      return;
    }
    const newLines: CartLine[] = request.cartSnapshot.map((l: PendingCartLine) => {
      const catalogueItem = liveCatalogue.find((it) => it.id === l.productId);
      const unit = l.basePrice + l.priceDelta + l.addons.reduce((n, a) => n + a.priceCents, 0);
      return {
        id: (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`,
        itemId: l.productId,
        name: l.name,
        category: (catalogueItem?.category ?? 'all') as CategoryId,
        imageUrl: catalogueItem?.imageUrl,
        size: l.size,
        qty: l.qty,
        unitPriceCents: unit,
        excludedIngredients: l.excludes,
        addedAddons: l.addons,
        note: l.note,
        expanded: false,
      };
    });
    // Append rather than replace — cashier might be mid-order.
    setCart((prev) => [...prev, ...newLines]);
    acceptedRequestRef.current = {
      id: request.id,
      orderCode: request.orderCode,
      table: { name: request.table.name },
    };
    // Snap the cart open so the cashier sees the import immediately.
    setCartOpen(true);
  };

  const openEditForLine = (line: CartLine) => {
    const item = liveCatalogue.find((it) => it.id === line.itemId);
    if (!item) return;
    setEditingLineId(line.id);
    setDetailsItem(item);
  };

  const updateCustomised = (lineId: string, cust: ProductCustomisation) => {
    setCart((c) => c.map((l) => l.id === lineId ? {
      ...l,
      size: cust.size,
      qty: cust.qty,
      unitPriceCents: cust.unitPriceCents,
      excludedIngredients: cust.excludedIngredients,
      addedAddons: cust.addedAddons,
      note: cust.note || undefined,
    } : l));
  };

  // VAT-INCLUSIVE pricing: line prices already include 15% VAT.
  // We compute the VAT portion BACK out of the total (not added on top).
  // See client/src/lib/vat.ts for the single helper.
  const grossCents = cart.reduce((s, l) => s + l.unitPriceCents * l.qty, 0);
  // Cap discount to gross so we never go negative if the cart shrinks
  // below the previously-applied amount.
  const effectiveDiscountCents = Math.min(orderDiscountCents, grossCents);
  const totalCents = Math.max(0, grossCents - effectiveDiscountCents);
  const taxCents = vatPortionOfInclusive(totalCents);
  const subtotalCents = totalCents - taxCents;

  const ORDER_TYPES: { value: typeof orderType; label: string; Icon: typeof IconGlass }[] = [
    { value: 'Dine In',   label: 'Dine In',   Icon: IconGlass },
    { value: 'Take Away', label: 'Take Away', Icon: IconShoppingBag },
    { value: 'Delivery',  label: 'Delivery',  Icon: IconTruck },
    { value: 'Table',     label: 'Table',     Icon: IconArmchair2 },
  ];

  const productDetailsPayload: ProductDetails | null = detailsItem
    ? {
        id: detailsItem.id,
        name: detailsItem.name,
        description: detailsItem.description,
        category: liveCategoryColours[detailsItem.category]?.label ?? '',
        basePriceCents: detailsItem.priceCents,
        imageUrl: detailsItem.imageUrl,
        bgClassName: liveCategoryColours[detailsItem.category]?.tile ?? 'bg-light',
        sizes: detailsItem.sizes,
        defaultIngredients: detailsItem.defaultIngredients,
        availableAddons: detailsItem.availableAddons,
      }
    : null;

  return (
    <div className="h-[100dvh] flex flex-col bg-light overflow-hidden">
      {/* Topbar (Phase 7-density redesign).
          Density ladder:
            ≥ xl   → full text labels everywhere
            md→xl  → status buttons label, nav items label, less padding
            sm→md  → status buttons icon-only, nav labels hidden, day buttons stay
            < sm   → only brand + day-state + sign-out; rest in overflow menu (future)
          Every icon has real behaviour. No template decoration.
      */}
      <header className="h-16 bg-surface border-b border-border px-3 sm:px-5 flex items-center gap-2 sm:gap-3 flex-shrink-0">
        {/* Brand — logo only (no text). Clicking goes to /admin for MANAGER+. */}
        {hasMinRole('MANAGER') ? (
          <button onClick={() => navigate('/admin')} className="flex-shrink-0" title="Admin dashboard" aria-label="Admin dashboard">
            <img src="/icons/icon-96.png" alt="CODED" className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl shadow-sm" />
          </button>
        ) : (
          <img src="/icons/icon-96.png" alt="CODED" className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl shadow-sm flex-shrink-0" />
        )}

        {/* Admin shortcut — grid icon. Only shown to MANAGER+ since they're the only ones with admin access. */}
        <RequireMinRole role="MANAGER">
          <button
            onClick={() => navigate('/admin')}
            className="w-10 h-10 inline-flex items-center justify-center rounded-lg bg-light text-gray-700 hover:bg-primary hover:text-[#FFFFFF] transition-colors flex-shrink-0"
            title="Open admin dashboard"
            aria-label="Open admin"
          >
            <IconGridDots size={18} stroke={2} />
          </button>
        </RequireMinRole>

        {/* Top nav — POS + Kitchen + Orders. Reservations and Tables removed (no features). */}
        <nav className="flex items-center gap-1 mx-auto min-w-0">
          {(
            [
              { label: 'POS',     to: '/pos',          active: true,  Icon: IconShoppingCart, minRole: 'CASHIER' as UserRole },
              { label: 'Kitchen', to: '/kds',          active: false, Icon: IconChefHat,      minRole: 'SUPERVISOR' as UserRole },
              { label: 'Orders',  to: '/admin/orders', active: false, Icon: IconReceipt2,     minRole: 'MANAGER' as UserRole },
            ] as const
          )
            .filter((item) => hasMinRole(item.minRole))
            .map(({ label, to, active, Icon }) => (
              <button
                key={label}
                onClick={() => navigate(to)}
                className={[
                  'h-10 sm:h-11 px-2.5 sm:px-3 lg:px-4 inline-flex items-center gap-2 rounded-lg text-13 lg:text-14 font-bold transition-colors',
                  active ? 'bg-primary-soft text-primary shadow-sm' : 'text-heading hover:bg-gray-100',
                ].join(' ')}
                title={label}
              >
                <Icon size={16} stroke={1.75} />
                <span className="hidden lg:inline">{label}</span>
              </button>
            ))}
        </nav>

        {/* Right-hand cluster */}
        <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
          <ConnectionStatusBadge />

          {/* Day-session actions — SUPERVISOR+. */}
          <RequireMinRole role="SUPERVISOR">
            {daySession ? (
              <div className="flex items-center gap-1 bg-light rounded-lg p-0.5 border border-border">
                <button
                  onClick={() => setDrawerModalOpen(true)}
                  className="h-9 px-2.5 lg:px-3 inline-flex items-center gap-1.5 rounded-md text-info text-12 font-bold uppercase tracking-wider hover:bg-info hover:text-[#FFFFFF] transition-colors"
                  title="Drawer · paid in / paid out"
                >
                  <IconCash size={14} stroke={2.25} />
                  <span className="hidden lg:inline">Drawer</span>
                </button>
                <button
                  onClick={() => setCloseDayModalOpen(true)}
                  className="h-9 px-2.5 lg:px-3 inline-flex items-center gap-1.5 rounded-md bg-warning text-[#FFFFFF] text-12 font-bold uppercase tracking-wider hover:bg-warning-hover transition-colors shadow-sm"
                  title="Close day session"
                >
                  <IconMoon size={14} stroke={2.25} />
                  <span className="hidden lg:inline">Close day</span>
                </button>
              </div>
            ) : (
              <button
                onClick={() => setOpenDayModalOpen(true)}
                className="h-10 px-3 inline-flex items-center gap-1.5 rounded-lg bg-success-soft border border-success/30 text-success text-12 font-bold uppercase tracking-wider hover:bg-success hover:text-[#FFFFFF] transition-colors"
                title="Open day session"
              >
                <IconSunHigh size={14} stroke={2.5} />
                <span className="hidden md:inline">Open day</span>
              </button>
            )}
          </RequireMinRole>

          {/* Utility icons */}
          <div className="hidden sm:flex items-center gap-0.5 bg-light rounded-lg p-1 border border-border">
            {/* X-Report (was the standalone button + the "Quick Stats" icon — now one) */}
            <RequireMinRole role="SUPERVISOR">
              <button
                onClick={() => setXReportOpen(true)}
                disabled={!daySession}
                className="w-9 h-9 inline-flex items-center justify-center rounded-md text-gray-700 hover:bg-surface hover:text-primary disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                title="X-Report · today's takings (supervisor+)"
                aria-label="X-Report"
              >
                <IconChartBar size={18} stroke={1.75} />
              </button>
            </RequireMinRole>

            {/* Recent orders toggle */}
            <button
              onClick={() => setRecentOpen((v) => !v)}
              className={[
                'w-9 h-9 inline-flex items-center justify-center rounded-md transition-colors',
                recentOpen ? 'bg-primary-soft text-primary' : 'text-gray-700 hover:bg-surface hover:text-primary',
              ].join(' ')}
              title={recentOpen ? 'Hide recent orders' : 'Show recent orders'}
              aria-pressed={recentOpen}
              aria-label="Toggle recent orders"
            >
              {recentOpen ? <IconEye size={18} stroke={1.75} /> : <IconEyeOff size={18} stroke={1.75} />}
            </button>

            {/* Theme */}
            <button
              onClick={toggleTheme}
              className="w-9 h-9 inline-flex items-center justify-center rounded-md text-gray-700 hover:bg-surface hover:text-primary transition-colors"
              title={theme === 'dark' ? 'Light mode' : 'Dark mode'}
              aria-label="Toggle theme"
            >
              {theme === 'dark' ? <IconSun size={18} stroke={1.75} /> : <IconMoon size={18} stroke={1.75} />}
            </button>

            {/* Fullscreen — wired */}
            <FullscreenButton />

            {/* Notifications — MANAGER+. Real content from the server. */}
            <RequireMinRole role="MANAGER">
              <NotificationsButton />
            </RequireMinRole>
          </div>

          {/* Avatar */}
          <div
            className="hidden md:flex w-10 h-10 rounded-full bg-primary-soft text-primary items-center justify-center text-13 font-bold ring-2 ring-surface shadow-sm flex-shrink-0"
            title={auth?.cashierName ?? 'Cashier'}
          >
            {cashierInitials}
          </div>

          {/* Sign out */}
          <button
            onClick={lockOut}
            className="w-10 h-10 inline-flex items-center justify-center rounded-lg text-gray-700 hover:bg-danger-soft hover:text-danger transition-colors flex-shrink-0"
            title="Sign out"
            aria-label="Sign out"
          >
            <IconLogout size={18} stroke={1.75} />
          </button>
        </div>
      </header>

      {/* Customer requests from the QR menu (Phase 6.3) */}
      <PendingRequestsBar onAccept={handleAcceptRequest} />

      {/* Mobile cart backdrop */}
      {cartOpen && (
        <button
          type="button"
          onClick={() => setCartOpen(false)}
          aria-label="Close cart"
          className="lg:hidden fixed inset-0 z-30 bg-black/40"
        />
      )}

      {/* Body */}
      <div className="flex-1 flex min-h-0 overflow-hidden">
        {/* LEFT — products */}
        <main className="flex-1 overflow-y-auto p-4 pr-3">
          {recentOpen && (
            <section className="mb-6 pb-6 border-b border-border">
              <div className="flex items-center justify-between flex-wrap gap-4 mb-4">
                <h3 className="text-24 font-bold text-heading mb-0 leading-none">Recent Orders</h3>
                <div className="flex items-center gap-2">
                  {(['All Orders', 'Dine In', 'Take Away', 'Delivery', 'Table'] as const).map((t) => {
                    const active = recentFilter === t;
                    return (
                      <button
                        key={t}
                        onClick={() => setRecentFilter(t)}
                        className={[
                          'h-11 px-5 rounded-lg text-14 font-semibold transition-all',
                          active ? 'bg-primary text-[#FFFFFF] shadow-md' : 'bg-surface border border-border text-heading hover:border-gray-300 shadow-sm',
                        ].join(' ')}
                      >
                        {t}
                      </button>
                    );
                  })}
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => scrollByX(recentScrollRef, -300)} className="w-10 h-10 inline-flex items-center justify-center rounded-lg bg-surface border border-border text-gray-700 hover:text-primary hover:border-primary shadow-sm transition-colors">
                    <IconChevronLeft size={18} />
                  </button>
                  <button onClick={() => scrollByX(recentScrollRef, 300)} className="w-10 h-10 inline-flex items-center justify-center rounded-lg bg-surface border border-border text-gray-700 hover:text-primary hover:border-primary shadow-sm transition-colors">
                    <IconChevronRight size={18} />
                  </button>
                </div>
              </div>
              <div ref={recentScrollRef} className="flex gap-3 overflow-x-auto pb-1 snap-x snap-mandatory scroll-smooth">
                {visibleRecent.map((o) => (
                  <RecentOrderCard key={o.id} o={o} onOpen={() => setPreviewOrder(o)} isDemo={usingDemo} />
                ))}
              </div>
            </section>
          )}

          {/* Menu Categories */}
          <div className="flex items-center justify-between flex-wrap gap-4 mb-4">
            <h5 className="text-24 font-bold text-heading mb-0 leading-none">Menu Categories</h5>
            <div className="flex items-center gap-5">
              <DietToggle label="Veg"     dotColor="border-success" active={diet.veg}    onClick={() => setDiet((d) => ({ ...d, veg: !d.veg }))} />
              <DietToggle label="Non Veg" dotColor="border-danger"  active={diet.nonVeg} onClick={() => setDiet((d) => ({ ...d, nonVeg: !d.nonVeg }))} />
              <DietToggle label="Egg"     dotColor="border-warning" active={diet.egg}    onClick={() => setDiet((d) => ({ ...d, egg: !d.egg }))} />
            </div>
            <div className="flex items-center gap-2">
              {/* Search trigger — opens the command-palette modal.
                  Visually a search input but functionally a button so
                  one tap puts focus straight inside the live search. */}
              <button
                type="button"
                onClick={() => setSearchOpen(true)}
                className="group relative w-72 h-11 pl-4 pr-3 rounded-lg bg-light border-2 border-border hover:border-primary text-left flex items-center gap-2 outline-none transition-colors shadow-sm"
                aria-label="Search products"
              >
                <IconSearch size={16} stroke={2.25} className="text-gray-500 group-hover:text-primary transition-colors flex-shrink-0" />
                <span className="flex-1 text-14 font-semibold text-gray-500 group-hover:text-heading transition-colors">
                  {search || 'Search products…'}
                </span>
                <span className="hidden md:inline-flex items-center gap-1 text-11 font-bold text-gray-500 px-2 h-6 rounded border border-border bg-surface">
                  <span className="text-12 leading-none">⌘</span>K
                </span>
              </button>
              <button className="w-11 h-11 inline-flex items-center justify-center rounded-full bg-surface border border-border text-gray-700 hover:text-primary hover:border-primary shadow-sm transition-colors" aria-label="Refresh">
                <IconRefresh size={18} />
              </button>
              <button className="w-11 h-11 inline-flex items-center justify-center rounded-full bg-surface border border-border text-gray-700 hover:text-primary hover:border-primary shadow-sm transition-colors" aria-label="Filter">
                <IconAdjustmentsHorizontal size={18} />
              </button>
              <button onClick={() => scrollByX(categoryScrollRef, -260)} className="w-11 h-11 inline-flex items-center justify-center rounded-full bg-surface border border-border text-gray-700 hover:text-primary hover:border-primary shadow-sm transition-colors">
                <IconChevronLeft size={18} />
              </button>
              <button onClick={() => scrollByX(categoryScrollRef, 260)} className="w-11 h-11 inline-flex items-center justify-center rounded-full bg-surface border border-border text-gray-700 hover:text-primary hover:border-primary shadow-sm transition-colors">
                <IconChevronRight size={18} />
              </button>
            </div>
          </div>

          {/* Category slider — one source of truth, colours flow through */}
          <div ref={categoryScrollRef} className="flex gap-3 overflow-x-auto mb-6 pb-2 snap-x snap-mandatory scroll-smooth">
            {liveCategoryList.map(({ id, label, count, Icon, iconBg }) => {
              const active = activeCategory === id;
              return (
                <button
                  key={id}
                  onClick={() => setActiveCategory(id)}
                  className={[
                    'flex items-center gap-3 px-3 py-2.5 rounded-lg border whitespace-nowrap transition-colors flex-shrink-0 snap-start shadow-sm',
                    active ? 'bg-surface border-primary' : 'bg-surface border-border hover:border-gray-300',
                  ].join(' ')}
                >
                  <span className={`w-12 h-12 rounded-full inline-flex items-center justify-center flex-shrink-0 ${iconBg}`}>
                    <Icon size={22} stroke={1.75} />
                  </span>
                  <div className="text-left">
                    <h6 className="text-15 font-bold text-heading mb-0 leading-tight">{label}</h6>
                    <p className="text-12 font-semibold text-heading/80 mb-0 leading-tight mt-0.5">{count} Menus</p>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Product grid — 8" tablet sweet spot is 3 cols (lg-xl); 4 cols only
              once we're on a proper desktop (2xl, 1536+). At md (768-1023) we
              also stay at 3 cols. */}
          <div className="grid grid-cols-2 md:grid-cols-3 2xl:grid-cols-4 gap-3">
            {visible.map((item) => (
              <ProductCard
                key={item.id}
                item={item}
                qty={qtyOf(item.id)}
                onInc={() => quickInc(item)}
                onDec={() => quickDec(item)}
                onOpen={() => setDetailsItem(item)}
                categoryColours={liveCategoryColours}
              />
            ))}
          </div>
        </main>

        {/* RIGHT — order panel.
            ≥lg: static side rail (440px), always visible.
            <lg: fixed slide-over drawer toggled by the floating Cart button. */}
        <aside
          className={[
            'bg-surface border-l border-border flex flex-col overflow-hidden',
            // Cart rail width scales with screen — slimmer on 8" tablets
            // (lg, ~1024-1280), comfortable at xl, generous at 2xl+.
            'lg:static lg:translate-x-0 lg:w-[360px] xl:w-[420px] 2xl:w-[460px] lg:h-full lg:flex-shrink-0',
            'fixed inset-y-0 right-0 z-40 w-full max-w-md h-full transform transition-transform duration-200 shadow-2xl lg:shadow-none',
            cartOpen ? 'translate-x-0' : 'translate-x-full lg:translate-x-0',
          ].join(' ')}
        >
          {/* Title */}
          <div className="px-4 py-3 border-b border-border flex items-center justify-between flex-wrap gap-2 flex-shrink-0">
            <h6 className="text-16 font-bold text-heading mb-0">New order</h6>
            <div className="flex items-center gap-2">
              <p className="text-13 font-medium text-gray-700 mb-0 tabular-nums">{nowLabel}</p>
              {/* Mobile-only close */}
              <button
                type="button"
                onClick={() => setCartOpen(false)}
                className="lg:hidden w-9 h-9 inline-flex items-center justify-center rounded-md text-gray-700 hover:bg-light"
                aria-label="Close cart"
              >
                <IconX size={18} stroke={2.25} />
              </button>
            </div>
          </div>

          {/* Order type tabs — bigger + bolder */}
          <div className="px-4 py-3 border-b border-border flex-shrink-0">
            <div className="grid grid-cols-4 gap-1.5 bg-light rounded-lg p-1 mb-3">
              {ORDER_TYPES.map(({ value, label, Icon }) => {
                const active = orderType === value;
                return (
                  <button
                    key={value}
                    onClick={() => { setOrderType(value); if (value !== 'Table') setTableId(null); }}
                    className={[
                      'h-12 inline-flex items-center justify-center gap-2 rounded-md text-14 font-bold transition-all',
                      active ? 'bg-surface text-heading shadow-sm' : 'text-body hover:text-heading',
                    ].join(' ')}
                  >
                    <Icon size={16} stroke={2} />
                    {label}
                  </button>
                );
              })}
            </div>

            {/* Table picker — visible only when "Table" is the order type. */}
            {orderType === 'Table' && (
              <div className="mb-3">
                <label className="block text-12 font-bold uppercase tracking-wider text-gray-700 mb-1.5">
                  Choose table {tableId === null && <span className="text-danger">*</span>}
                </label>
                {tables.length === 0 ? (
                  <p className="text-13 italic text-gray-700">
                    No tables yet — add some in <a href="/admin/tables" className="text-primary underline">Admin → Tables</a>.
                  </p>
                ) : (
                  <select
                    value={tableId ?? ''}
                    onChange={(e) => setTableId(e.target.value || null)}
                    className={[
                      'w-full h-12 px-3 rounded-md bg-light border-2 outline-none text-14 font-semibold text-heading',
                      tableId ? 'border-border' : 'border-danger/40 focus:border-danger',
                    ].join(' ')}
                  >
                    <option value="">Select a table…</option>
                    {Array.from(new Set(tables.map((t) => t.area ?? 'No area'))).sort().map((area) => (
                      <optgroup key={area} label={area}>
                        {tables.filter((t) => (t.area ?? 'No area') === area).map((t) => (
                          <option key={t.id} value={t.id}>{t.name}</option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                )}
              </div>
            )}

            {/* Customer (Walk-in by default) — when unattached we
                render a confident black CTA with a minimal scan-style
                glyph so it doesn't look like an empty placeholder.
                Once a customer is attached the row switches to a
                lighter "info row" treatment so the cart UI doesn't
                look button-heavy. */}
            {customer ? (
              <button
                type="button"
                onClick={() => setCustomerOpen(true)}
                className="w-full flex items-center gap-3 px-3 h-12 rounded-md bg-light hover:bg-gray-100 border border-border transition-colors"
              >
                <div className="w-9 h-9 rounded-full inline-flex items-center justify-center flex-shrink-0 text-13 font-bold bg-primary text-[#FFFFFF]">
                  {customer.name.split(' ').map((p) => p[0]).slice(0, 2).join('').toUpperCase()}
                </div>
                <div className="flex-1 min-w-0 text-left">
                  <p className="text-14 font-bold text-heading truncate leading-tight">{customer.name}</p>
                  <p className="text-12 font-semibold text-heading/80 mt-0.5 leading-tight">
                    {customer.phone || 'No phone on file'}
                  </p>
                </div>
                {typeof customer.loyaltyPoints === 'number' && (
                  <span className="inline-flex items-center gap-1 text-12 font-bold text-warning">
                    <IconStar size={12} stroke={2} fill="currentColor" />
                    {customer.loyaltyPoints}
                  </span>
                )}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setCustomerOpen(true)}
                className="w-full flex items-center gap-3 px-5 h-12 rounded-md bg-danger hover:bg-danger-hover text-[#FFFFFF] shadow-sm transition-colors"
              >
                <span className="w-8 h-8 rounded-md inline-flex items-center justify-center flex-shrink-0 bg-white/15">
                  <IconUserPlus size={18} stroke={2.25} />
                </span>
                <span className="flex-1 min-w-0 text-left">
                  <span className="block text-14 font-bold leading-none">Walk-in Customer</span>
                  <span className="block text-12 font-semibold text-white/80 leading-none mt-1.5">Tap to attach &amp; earn loyalty points</span>
                </span>
                <IconChevronRight size={16} stroke={2.5} className="text-white/80 flex-shrink-0" />
              </button>
            )}
          </div>

          {/* Ordered Menus */}
          <div className="px-4 py-3 border-b border-border flex items-center justify-between flex-shrink-0">
            <h6 className="text-15 font-bold text-heading mb-0">Ordered Menus</h6>
            <p className="text-13 font-semibold text-heading mb-0 inline-flex items-center gap-2 whitespace-nowrap">
              Total Menus :
              <span className="inline-flex items-center justify-center min-w-[28px] h-7 px-2 rounded-full border border-border text-13 font-bold text-heading tabular-nums">{cart.length}</span>
            </p>
          </div>

          <div className="flex-1 overflow-y-auto px-3 py-3">
            {cart.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-center px-6">
                <IconScan size={32} className="text-gray-400 mb-3" />
                <p className="text-15 font-semibold text-heading">No items yet</p>
                <p className="text-13 text-body mt-1">Tap a product or scan a barcode</p>
              </div>
            ) : (
              <ul className="space-y-2">
                {cart.map((line) => {
                  const cat = liveCategoryColours[line.category] ?? { tile: 'bg-light', eyebrow: 'text-gray-700', iconBg: 'bg-light text-gray-700', label: '', Icon: IconSparkles };
                  return (
                    <li key={line.id} className={[
                      'p-2.5 rounded-md border shadow-sm transition-colors',
                      line.expanded ? 'bg-surface border-primary' : 'bg-surface border-border',
                    ].join(' ')}>
                      <div className="flex items-center justify-between gap-2">
                        <button onClick={() => toggleExpand(line.id)} className="flex items-center gap-2.5 overflow-hidden flex-1 min-w-0 text-left">
                          <div className="w-12 h-12 rounded-md overflow-hidden flex-shrink-0">
                            <ProductImage src={line.imageUrl} name={line.name} seed={line.itemId} bgClassName={cat.tile} compact="initials" />
                          </div>
                          <div className="overflow-hidden leading-tight">
                            <h6 className="text-15 font-bold text-heading truncate">{line.name}</h6>
                            <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                              <span className="inline-flex items-center text-12 font-bold uppercase tracking-widest px-2 h-5 rounded bg-gray-100 text-heading">
                                {line.size}
                              </span>
                              {(line.excludedIngredients.length > 0 || line.addedAddons.length > 0 || line.note) && (
                                <span className="inline-flex items-center gap-1 text-11 font-bold uppercase tracking-wider px-2 h-5 rounded bg-warning text-[#FFFFFF]">
                                  <IconAdjustmentsHorizontal size={11} stroke={2.5} />
                                  Custom
                                </span>
                              )}
                            </div>
                          </div>
                        </button>
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          <Stepper qty={line.qty} onInc={() => incLine(line.id)} onDec={() => decLine(line.id)} size="sm" />
                          <button
                            onClick={() => openEditForLine(line)}
                            className="w-7 h-7 inline-flex items-center justify-center rounded-full bg-light text-gray-500 hover:bg-primary hover:text-[#FFFFFF] transition-colors"
                            title="Edit options"
                            aria-label="Edit options"
                          >
                            <IconPencil size={12} stroke={2.5} />
                          </button>
                          <button
                            onClick={() => removeLine(line.id)}
                            className="w-7 h-7 inline-flex items-center justify-center rounded-full bg-light text-gray-500 hover:bg-danger hover:text-[#FFFFFF] transition-colors"
                            title="Remove"
                            aria-label="Remove"
                          >
                            <IconX size={12} stroke={2.5} />
                          </button>
                        </div>
                      </div>

                      {line.expanded && (
                        <div className="mt-2.5 pt-2.5 border-t border-border-soft space-y-2">
                          {/* Customisations */}
                          {(line.excludedIngredients.length > 0 || line.addedAddons.length > 0 || line.note) && (
                            <div className="space-y-1.5">
                              {line.excludedIngredients.length > 0 && (
                                <div>
                                  <p className="text-10 font-bold uppercase tracking-widest text-danger mb-1">Excluded</p>
                                  <div className="flex flex-wrap gap-1">
                                    {line.excludedIngredients.map((ing) => (
                                      <span key={ing} className="inline-flex items-center gap-1 px-2 h-6 rounded-md bg-danger text-[#FFFFFF] text-11 font-bold">
                                        <IconX size={11} stroke={2.5} />
                                        No {ing}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                              {line.addedAddons.length > 0 && (
                                <div>
                                  <p className="text-10 font-bold uppercase tracking-widest text-success mb-1">Add-ons</p>
                                  <div className="flex flex-wrap gap-1">
                                    {line.addedAddons.map((a) => (
                                      <span key={a.name} className="inline-flex items-center gap-1 px-2 h-6 rounded-md bg-success text-[#FFFFFF] text-11 font-bold">
                                        + {a.name}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                              {line.note && (
                                <div>
                                  <p className="text-10 font-bold uppercase tracking-widest text-warning mb-1">Note</p>
                                  <span className="inline-flex items-center px-2 h-6 rounded-md bg-warning text-[#FFFFFF] text-11 font-bold italic">
                                    "{line.note}"
                                  </span>
                                </div>
                              )}
                            </div>
                          )}
                          {/* Money breakdown — Item Rate is tappable for a
                              supervisor-approved price override. */}
                          <div className="flex items-center justify-between">
                            <button
                              type="button"
                              onClick={() => setOverrideLineId(line.id)}
                              className="text-center group focus:outline-none"
                              title={line.priceOverride ? 'Edit / revert price override' : 'Override price (supervisor PIN)'}
                            >
                              <span className="text-12 font-bold uppercase tracking-wider text-heading block mb-1 group-hover:text-primary transition-colors">
                                Item Rate
                              </span>
                              <p className="text-15 font-bold mb-0 tabular-nums text-heading group-hover:text-primary underline decoration-dotted underline-offset-4">
                                {fmtR(line.unitPriceCents)}
                              </p>
                              {line.priceOverride && (
                                <span className="inline-flex items-center gap-1 mt-1 px-1.5 h-4 rounded bg-warning text-[#FFFFFF] text-10 font-bold uppercase tracking-wider">
                                  Override · {line.unitPriceCents < line.priceOverride.originalCents ? '−' : '+'}{fmtR(Math.abs(line.unitPriceCents - line.priceOverride.originalCents))}
                                </span>
                              )}
                            </button>
                            <div className="text-center">
                              <span className="text-12 font-bold uppercase tracking-wider text-heading block mb-1">Amount</span>
                              <p className="text-15 font-bold text-heading mb-0 tabular-nums">{fmtR(line.unitPriceCents * line.qty)}</p>
                            </div>
                            <div className="text-center">
                              <span className="text-12 font-bold uppercase tracking-wider text-heading block mb-1">Total</span>
                              <p className="text-16 font-bold text-primary mb-0 tabular-nums">{fmtR(line.unitPriceCents * line.qty)}</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* Payment summary + CTA + collapsible actions */}
          <div className="px-4 pt-3 pb-4 border-t border-border flex-shrink-0">
            {/* Compact header — collapsed by default. Prices are VAT-inclusive
                everywhere so the subtotal/VAT breakdown is detail, not headline. */}
            <div className="flex items-center justify-between mb-2">
              <button
                type="button"
                onClick={() => setSummaryOpen((o) => !o)}
                className="inline-flex items-center gap-1.5 text-12 font-bold uppercase tracking-widest text-gray-700 hover:text-heading transition-colors"
                aria-expanded={summaryOpen}
              >
                <IconChevronDown size={14} stroke={2.5} className={`transition-transform ${summaryOpen ? '' : '-rotate-90'}`} />
                Payment summary
              </button>
              <button
                type="button"
                disabled={cart.length === 0}
                onClick={() => setDiscountModalOpen(true)}
                className={[
                  'h-8 px-3 inline-flex items-center gap-1.5 rounded-md text-12 font-bold uppercase tracking-wider transition-colors',
                  effectiveDiscountCents > 0
                    ? 'bg-success text-[#FFFFFF] hover:bg-success-hover'
                    : 'bg-light border border-border text-heading hover:bg-gray-100',
                  cart.length === 0 ? 'opacity-40 cursor-not-allowed' : '',
                ].join(' ')}
                title="Apply order discount"
              >
                <IconPercentage size={14} stroke={2.25} />
                {effectiveDiscountCents > 0 ? `−${fmtR(effectiveDiscountCents)}` : 'Discount'}
              </button>
            </div>

            {summaryOpen && (
              <div className="space-y-1 mb-3 pl-1">
                {effectiveDiscountCents > 0 && (
                  <p className="flex items-center justify-between mb-0 text-14 font-semibold text-heading">
                    Cart gross
                    <span className="text-14 font-semibold text-gray-700 line-through tabular-nums">{fmtR(grossCents)}</span>
                  </p>
                )}
                {effectiveDiscountCents > 0 && (
                  <p className="flex items-center justify-between mb-0 text-14 font-semibold text-success">
                    Discount{orderDiscountReason ? ` · ${orderDiscountReason}` : ''}
                    <span className="text-15 font-bold text-success tabular-nums">−{fmtR(effectiveDiscountCents)}</span>
                  </p>
                )}
                <p className="flex items-center justify-between mb-0 text-14 font-semibold text-heading">Sub Total (excl. VAT)<span className="text-15 font-bold text-heading tabular-nums">{fmtR(subtotalCents)}</span></p>
                <p className="flex items-center justify-between mb-0 text-14 font-semibold text-heading">VAT 15% (included)<span className="text-15 font-bold text-heading tabular-nums">{fmtR(taxCents)}</span></p>
              </div>
            )}

            <div className={`${summaryOpen ? 'border-t border-border pt-2.5' : ''} flex items-center justify-between mb-3`}>
              <h5 className="text-16 font-bold text-heading mb-0 leading-none">Amount to be Paid</h5>
              <p className="text-22 font-bold text-primary tabular-nums leading-none">{fmtR(totalCents)}</p>
            </div>

            <div className="flex items-stretch gap-2">
              <button
                type="button"
                disabled={cart.length === 0}
                onClick={() => setPaymentOpen(true)}
                className="flex-1 h-12 rounded-md bg-primary text-[#FFFFFF] text-16 font-semibold hover:bg-primary-hover disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                Place an Order
              </button>
              <button
                type="button"
                onClick={() => setMoreActionsOpen((o) => !o)}
                className={[
                  'w-12 h-12 inline-flex items-center justify-center rounded-md border transition-colors flex-shrink-0',
                  moreActionsOpen ? 'bg-primary-soft border-primary text-primary' : 'bg-surface border-border text-heading hover:border-gray-300',
                ].join(' ')}
                title="More order actions"
                aria-label="More order actions"
                aria-expanded={moreActionsOpen}
              >
                <IconChevronDown size={18} stroke={2} className={moreActionsOpen ? 'rotate-180 transition-transform' : 'transition-transform'} />
              </button>
            </div>

            {moreActionsOpen && (
              <div className="grid grid-cols-3 gap-1.5 mt-2 animate-fade-in">
                <button className="h-10 inline-flex items-center justify-center gap-1.5 rounded-md border border-border bg-surface text-13 font-bold text-heading hover:bg-light transition-colors shadow-sm"><IconPrinter size={14} stroke={2.25} /> Print</button>
                <button className="h-10 inline-flex items-center justify-center gap-1.5 rounded-md border border-border bg-surface text-13 font-bold text-heading hover:bg-light transition-colors shadow-sm"><IconFileInvoice size={14} stroke={2.25} /> Invoice</button>
                <button className="h-10 inline-flex items-center justify-center gap-1.5 rounded-md border border-border bg-surface text-13 font-bold text-heading hover:bg-light transition-colors shadow-sm"><IconFiles size={14} stroke={2.25} /> Draft</button>
                <button className="h-10 inline-flex items-center justify-center gap-1.5 rounded-md border border-border bg-surface text-13 font-bold text-heading hover:bg-danger-soft hover:text-danger hover:border-danger/30 transition-colors shadow-sm"><IconX size={14} stroke={2.5} /> Cancel</button>
                <button className="h-10 inline-flex items-center justify-center gap-1.5 rounded-md border border-border bg-surface text-13 font-bold text-heading hover:bg-light transition-colors shadow-sm"><IconBolt size={14} stroke={2.25} /> Void</button>
                <button className="h-10 inline-flex items-center justify-center gap-1.5 rounded-md border border-border bg-surface text-13 font-bold text-heading hover:bg-light transition-colors shadow-sm"><IconHistory size={14} stroke={2.25} /> History</button>
              </div>
            )}
          </div>
        </aside>
      </div>

      {/* Command-palette product search — opens from the toolbar
          search button or `/` / ⌘K. Selecting a result opens the
          modifier modal; quick-add (+ button or ⌘⏎) appends a line
          at default size/qty 1. */}
      <SearchModal
        open={searchOpen}
        items={searchableItems}
        onClose={() => setSearchOpen(false)}
        onOpenDetails={(it) => {
          const item = liveCatalogue.find((c) => c.id === it.id);
          if (item) setDetailsItem(item);
        }}
        onQuickAdd={(it) => {
          const item = liveCatalogue.find((c) => c.id === it.id);
          if (item) quickInc(item);
        }}
      />

      {/* Customer popover */}
      <CustomerPopover
        open={customerOpen}
        onClose={() => setCustomerOpen(false)}
        onAttach={(c) => setCustomer(c)}
        onClear={() => setCustomer(null)}
        selectedCustomer={customer}
      />

      {/* Product details modal — doubles as add + edit. When
          editingLineId is set, the modal is pre-populated and the
          CTA reads "Update". */}
      <ProductDetailsModal
        open={detailsItem !== null}
        product={productDetailsPayload}
        editing={editingLineId
          ? (() => {
              const l = cart.find((x) => x.id === editingLineId);
              if (!l) return null;
              return {
                size: l.size,
                unitPriceCents: l.unitPriceCents,
                excludedIngredients: l.excludedIngredients,
                addedAddons: l.addedAddons,
                note: l.note ?? '',
                qty: l.qty,
              };
            })()
          : null}
        onClose={() => { setDetailsItem(null); setEditingLineId(null); }}
        onAdd={(cust) => {
          if (editingLineId) updateCustomised(editingLineId, cust);
          else if (detailsItem) addCustomised(detailsItem, cust);
        }}
      />

      {/* Recent-order summary modal — opens when a Recent Orders card
          is tapped. Read-only: items, status, payment, total. */}
      <Modal open={previewOrder !== null} onClose={() => setPreviewOrder(null)} size="lg" hideClose>
        {previewOrder && (() => {
          const o = previewOrder;
          // VAT-inclusive — see lib/vat.ts
          const total = o.items.reduce((s, it) => s + it.priceCents * it.qty, 0);
          const tax = vatPortionOfInclusive(total);
          const subtotal = total - tax;
          const statusTone =
            o.status === 'Delayed' ? 'bg-danger text-[#FFFFFF]' :
            o.status === 'Ready' || o.status === 'Awaiting collection' ? 'bg-success text-[#FFFFFF]' :
            o.status === 'Out for delivery' ? 'bg-info text-[#FFFFFF]' :
            'bg-warning text-[#FFFFFF]';
          return (
            <div className="-m-6">
              {/* Header band */}
              <div className="px-6 pt-6 pb-5 border-b border-border">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <p className="text-12 font-bold uppercase tracking-wider text-gray-500 mb-1">Order</p>
                    <h2 className="text-26 font-bold text-heading tabular-nums leading-tight">{o.id}</h2>
                    <p className="text-14 font-semibold text-gray-700 mt-1">
                      {o.customer} · placed {o.time}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-2 flex-shrink-0">
                    <span className={`inline-flex items-center text-12 font-bold uppercase tracking-widest px-3 h-7 rounded ${statusTone} shadow-sm`}>
                      {o.status}
                    </span>
                    <span className="inline-flex items-center gap-1.5 text-13 font-bold text-heading">
                      <IconChecks size={14} stroke={2.25} /> {o.type}
                    </span>
                  </div>
                  <button
                    onClick={() => setPreviewOrder(null)}
                    className="w-9 h-9 inline-flex items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 transition-colors flex-shrink-0"
                    aria-label="Close"
                  >
                    <IconX size={18} stroke={2} />
                  </button>
                </div>

                {/* Progress bar */}
                <div className="mt-4">
                  <div className="flex items-center justify-between text-13 font-semibold text-heading mb-1.5">
                    <span className="inline-flex items-center gap-1.5">
                      <IconClock size={14} stroke={2.25} /> {o.elapsed} elapsed
                    </span>
                    <span className={o.delayed ? 'text-danger' : 'text-success'}>
                      {o.delayed ? 'Past due — ' : ''}{o.remaining}
                    </span>
                  </div>
                  <div className="h-2 rounded-full bg-light overflow-hidden">
                    <div
                      className={`h-full rounded-full ${o.delayed ? 'bg-danger' : 'bg-success'}`}
                      style={{ width: `${o.progress}%` }}
                    />
                  </div>
                </div>
              </div>

              {/* Items */}
              <div className="px-6 py-5">
                <h3 className="text-14 font-bold text-heading mb-3">
                  Items <span className="text-13 font-semibold text-gray-600">({o.items.length})</span>
                </h3>
                <ul className="space-y-2">
                  {o.items.map((it, i) => {
                    const cat = liveCategoryColours[it.category] ?? { tile: 'bg-light', eyebrow: 'text-gray-700', iconBg: 'bg-light text-gray-700', label: '', Icon: IconSparkles };
                    return (
                      <li key={i} className="flex items-center gap-3 p-3 rounded-md border border-border bg-surface">
                        <div className="w-11 h-11 rounded-md overflow-hidden flex-shrink-0">
                          <ProductImage name={it.name} seed={it.name} bgClassName={cat.tile} compact="initials" />
                        </div>
                        <div className="flex-1 min-w-0 leading-tight">
                          <p className="text-15 font-bold text-heading truncate">{it.name}</p>
                          <span className="inline-flex items-center text-12 font-bold uppercase tracking-widest px-2 h-5 rounded bg-gray-100 text-heading mt-0.5">
                            {it.size}
                          </span>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-15 font-bold text-heading tabular-nums">
                            ×{it.qty}
                          </p>
                          <p className="text-13 font-semibold text-gray-700 tabular-nums mt-0.5">
                            {fmtR(it.priceCents * it.qty)}
                          </p>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>

              {/* Totals + payment */}
              <div className="px-6 pb-6 space-y-2 border-t border-border pt-5">
                <p className="flex items-center justify-between text-14 font-semibold text-heading mb-0">
                  Sub Total (excl. VAT)
                  <span className="text-15 font-bold text-heading tabular-nums">{fmtR(subtotal)}</span>
                </p>
                <p className="flex items-center justify-between text-14 font-semibold text-heading mb-0">
                  VAT 15% (included)
                  <span className="text-15 font-bold text-heading tabular-nums">{fmtR(tax)}</span>
                </p>
                <div className="flex items-center justify-between pt-3 border-t border-border">
                  <span className="text-16 font-bold text-heading">Amount paid</span>
                  <span className="text-22 font-bold text-primary tabular-nums">{fmtR(total)}</span>
                </div>
                <p className="flex items-center justify-between text-13 font-semibold text-gray-700 pt-1.5 mb-0">
                  Payment method
                  <span className="text-13 font-bold text-heading">{o.paymentMethod}</span>
                </p>
              </div>

              {/* Footer actions */}
              <div className="px-6 py-4 border-t border-border bg-light rounded-b-xl flex items-center gap-3">
                <button
                  onClick={() => setPreviewOrder(null)}
                  className="flex-1 h-12 px-5 rounded-md bg-surface border-2 border-border text-14 font-bold text-heading hover:border-gray-400 hover:bg-light transition-colors"
                >
                  Close
                </button>
                {o.apiId && hasMinRole('SUPERVISOR') && (
                  <button
                    onClick={() => { void openActionsForApiOrderId(o.apiId!); setPreviewOrder(null); }}
                    disabled={openingActionsFor === o.apiId}
                    className="flex-1 h-12 px-5 inline-flex items-center justify-center gap-2 rounded-md bg-surface border-2 border-border text-14 font-bold text-heading hover:border-danger hover:text-danger transition-colors disabled:opacity-50"
                  >
                    <IconBolt size={18} stroke={2.25} /> Cancel · Void · Refund
                  </button>
                )}
                <button
                  onClick={() => { setReceiptOrder(o); setPreviewOrder(null); }}
                  className="flex-1 h-12 px-5 inline-flex items-center justify-center gap-2 rounded-md bg-primary text-[#FFFFFF] text-14 font-bold hover:bg-primary-hover shadow-sm"
                >
                  <IconPrinter size={18} stroke={2.5} /> Print receipt
                </button>
              </div>
            </div>
          );
        })()}
      </Modal>

      {/* Cashier-blocking overlay when no day session is open */}
      <DayNotOpenOverlay />

      {/* Supervisor day-session modals */}
      <OpenDayModal open={openDayModalOpen} onClose={() => setOpenDayModalOpen(false)} />
      <CloseDayModal open={closeDayModalOpen} onClose={() => setCloseDayModalOpen(false)} />
      <DrawerModal open={drawerModalOpen} onClose={() => setDrawerModalOpen(false)} />
      <DayReportModal open={xReportOpen} onClose={() => setXReportOpen(false)} kind="X" />
      <OrderActionsModal
        open={actionsOrder !== null}
        order={actionsOrder}
        onClose={() => setActionsOrder(null)}
        onChanged={refreshRecent}
      />

      {/* Receipt preview / print — SARS-compliant tax invoice */}
      <Modal open={receiptOrder !== null} onClose={() => setReceiptOrder(null)} size="md" hideClose>
        {receiptOrder && (() => {
          const o = receiptOrder;
          return (
            <div className="-m-6">
              {/* Top bar — hidden when printing */}
              <div className="px-5 py-3 border-b border-border flex items-center justify-between print:hidden">
                <p className="text-15 font-bold text-heading">Tax Invoice · {o.id}</p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => window.print()}
                    className="h-10 px-4 inline-flex items-center gap-2 rounded-md bg-primary text-[#FFFFFF] text-13 font-bold hover:bg-primary-hover shadow-sm"
                  >
                    <IconPrinter size={16} stroke={2.5} /> Print
                  </button>
                  <button
                    onClick={() => setReceiptOrder(null)}
                    className="h-10 px-4 inline-flex items-center rounded-md bg-light border border-border text-13 font-bold text-heading hover:bg-gray-100"
                  >
                    Close
                  </button>
                </div>
              </div>
              {/* Receipt body */}
              <div className="bg-gray-100 print:bg-white py-6 print:p-0 receipt-print-root">
                <Receipt
                  business={BUSINESS_PROFILE}
                  receipt={{
                    orderNumber: o.id,
                    placedAt: new Date(),
                    customer: { name: o.customer },
                    paymentMethod: o.paymentMethod,
                    lines: o.items.map((it) => ({
                      name: it.name,
                      variant: it.size,
                      qty: it.qty,
                      unitPriceCents: it.priceCents,
                    })),
                  }}
                />
              </div>
            </div>
          );
        })()}
      </Modal>

      {/* Payment modal — Phase 2.2 */}
      <PaymentModal
        open={paymentOpen}
        onClose={() => setPaymentOpen(false)}
        totalCents={totalCents}
        channel={orderType === 'Take Away' ? 'TAKEAWAY' : orderType === 'Delivery' ? 'DELIVERY' : 'DINE_IN'}
        lines={cart.map((l) => ({
          productId: l.itemId,
          name: l.name,
          variantSize: l.size,
          qty: l.qty,
          unitPriceCents: l.unitPriceCents,
          excludedIngredients: l.excludedIngredients,
          addons: l.addedAddons,
          note: l.note ?? null,
          priceOverrideToken: l.priceOverride?.token ?? null,
        }))}
        customer={customer ? { id: customer.id, name: customer.name, phone: customer.phone, email: customer.email } : undefined}
        discountCents={effectiveDiscountCents}
        discountReason={orderDiscountReason}
        onSuccess={(order) => {
          setPaymentOpen(false);
          setLastPlacedOrder({ orderNumber: order.orderNumber, totalCents: order.totalCents });
          setCart([]);
          setOrderDiscountCents(0);
          setOrderDiscountReason('');
          // Open the receipt prompt — Print / Email / Skip.
          setReceiptStep('choose');
          setReceiptEmail(customer?.email ?? '');
          setOrderModal(true);
          refreshRecent();
        }}
      />

      <DiscountModal
        open={discountModalOpen}
        onClose={() => setDiscountModalOpen(false)}
        subtotalCents={grossCents}
        initialDiscountCents={orderDiscountCents}
        initialReason={orderDiscountReason}
        onApply={(d, r) => {
          setOrderDiscountCents(d);
          setOrderDiscountReason(r);
        }}
      />

      {/* Phase 2.6b — line price override (supervisor PIN inline) */}
      <PriceOverrideModal
        open={!!overrideLine}
        onClose={() => setOverrideLineId(null)}
        lineId={overrideLine?.id ?? ''}
        productId={overrideLine?.itemId ?? null}
        productName={overrideLine?.name ?? ''}
        variantSize={overrideLine?.size}
        currentUnitPriceCents={overrideLine?.unitPriceCents ?? 0}
        originalUnitPriceCents={overrideLine?.priceOverride?.originalCents ?? overrideLine?.unitPriceCents ?? 0}
        hasExistingOverride={!!overrideLine?.priceOverride}
        onAuthorised={applyPriceOverride}
        onRemove={removePriceOverride}
      />

      {/* Order placed + receipt prompt — choose / email-input / done */}
      <Modal open={orderModal} onClose={() => setOrderModal(false)} size="sm" hideClose>
        <div className="text-center py-6">
          <div className="w-16 h-16 rounded-full bg-success-soft text-success flex items-center justify-center mx-auto mb-4">
            <IconChecks size={32} stroke={2} />
          </div>
          <h3 className="text-20 font-bold text-heading">
            {receiptStep === 'sent' ? 'Receipt emailed' : receiptStep === 'printed' ? 'Sending to printer' : 'Order placed'}
          </h3>
          <p className="text-14 text-body mt-1.5">
            {lastPlacedOrder?.orderNumber
              ? <>Order <span className="font-bold">#{lastPlacedOrder.orderNumber}</span> · {`R ${(lastPlacedOrder.totalCents / 100).toFixed(2)}`}</>
              : 'Saved offline — will sync when back online.'}
          </p>

          {receiptStep === 'choose' && (
            <>
              <p className="text-13 font-bold uppercase tracking-widest text-gray-700 mt-5 mb-3">Receipt for the customer?</p>
              <div className="grid grid-cols-2 gap-2 px-2">
                <button
                  onClick={() => { setReceiptStep('printed'); /* TODO: integrate slip printer */ }}
                  className="h-14 inline-flex items-center justify-center gap-2 rounded-md bg-primary text-[#FFFFFF] text-14 font-bold hover:bg-primary-hover transition-colors shadow-sm"
                >
                  <IconPrinter size={18} stroke={2.25} />
                  Print
                </button>
                <button
                  onClick={() => {
                    // If we already have an email (from attached customer), skip the input step.
                    if (receiptEmail.trim()) setReceiptStep('email-input');
                    else setReceiptStep('email-input');
                  }}
                  className="h-14 inline-flex items-center justify-center gap-2 rounded-md bg-info text-[#FFFFFF] text-14 font-bold hover:bg-info-hover transition-colors shadow-sm"
                >
                  <IconMail size={18} stroke={2.25} />
                  Email
                </button>
              </div>
              <button onClick={() => setOrderModal(false)} className="mt-3 h-10 px-5 text-13 font-semibold text-gray-700 hover:text-heading underline">
                No receipt
              </button>
            </>
          )}

          {receiptStep === 'email-input' && (
            <div className="mt-5 text-left">
              <label className="block text-12 font-bold uppercase tracking-widest text-gray-700 mb-2">
                Customer email
                {customer?.email && <span className="ml-2 normal-case text-success tracking-normal">· auto-filled from loyalty</span>}
              </label>
              <input
                type="email"
                value={receiptEmail}
                onChange={(e) => setReceiptEmail(e.target.value)}
                placeholder="customer@email.com"
                className="w-full h-12 px-4 rounded-md bg-surface border-2 border-border focus:border-primary outline-none text-14 font-semibold text-heading placeholder-gray-500"
              />
              <div className="flex items-stretch gap-2 mt-4">
                <button
                  onClick={() => setReceiptStep('choose')}
                  className="flex-1 h-11 rounded-md bg-light border-2 border-border text-13 font-bold text-heading"
                >
                  Back
                </button>
                <button
                  disabled={!receiptEmail.trim() || !receiptEmail.includes('@')}
                  onClick={() => { setReceiptStep('sent'); /* TODO: POST email-receipt endpoint */ }}
                  className="flex-[2] h-11 rounded-md bg-info text-[#FFFFFF] text-13 font-bold inline-flex items-center justify-center gap-2 disabled:bg-gray-300"
                >
                  <IconMail size={16} stroke={2.5} />
                  Send to {receiptEmail.split('@')[0] || 'customer'}
                </button>
              </div>
            </div>
          )}

          {(receiptStep === 'sent' || receiptStep === 'printed') && (
            <div className="mt-5">
              <p className="text-13 font-medium text-success">
                {receiptStep === 'sent' ? `Sent to ${receiptEmail}` : 'Queued for the slip printer (printer integration coming soon).'}
              </p>
              <button onClick={() => setOrderModal(false)} className="mt-5 h-12 px-6 rounded-md bg-primary text-[#FFFFFF] text-14 font-bold hover:bg-primary-hover">
                Done
              </button>
            </div>
          )}
        </div>
      </Modal>

      {/* Mobile cart FAB — only shown <lg when the drawer is closed.
          Tap to open the drawer. Shows the line count badge. */}
      {!cartOpen && (
        <button
          type="button"
          onClick={() => setCartOpen(true)}
          className="lg:hidden fixed bottom-5 right-5 z-30 h-14 pl-4 pr-5 rounded-full bg-primary text-[#FFFFFF] text-15 font-bold inline-flex items-center gap-2 shadow-lg hover:bg-primary-hover active:scale-[0.98] transition-all"
          aria-label="Open cart"
        >
          <span className="relative inline-flex items-center justify-center w-9 h-9 rounded-full bg-[rgba(255,255,255,0.2)]">
            <IconShoppingCart size={20} stroke={2.25} />
            {cart.length > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 inline-flex items-center justify-center rounded-full bg-danger text-[#FFFFFF] text-10 font-bold tabular-nums ring-2 ring-primary">
                {cart.length}
              </span>
            )}
          </span>
          <span>{cart.length === 0 ? 'Cart' : `R ${(grossCents / 100).toFixed(2)}`}</span>
        </button>
      )}
    </div>
  );
}
